import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  MapContainer,
  Marker,
  Polyline,
  TileLayer,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useLocation } from "react-router-dom";
import io from "socket.io-client";
import L from "leaflet";
import "./PullApiTracking.css";
import { validateTrackingUrl } from "../Services";
import StartMarker from "../assets/start-marker.png";
import StopMarker from "../assets/stop-marker.png";
import axios from "axios";
import LatLonSpherical from "./map-conv/index";
import leafletImage from "leaflet-image";

// Marker icons
const MarkerIcon = L.icon({
  iconSize: [35, 40],
  iconAnchor: [10, 41],
  popupAnchor: [2, -40],
  iconUrl: StartMarker,
});

const StopMarkerIcon = L.icon({
  iconSize: [35, 40],
  iconAnchor: [10, 41],
  popupAnchor: [2, -40],
  iconUrl: StopMarker,
});

const LeafLetMapIconCa = L.divIcon({
  iconSize: [30, 40],
  html: '<div class="mapMarker"></div>',
});

// Utility for session storage
const setSessionStorageItem = (key, value) => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

const getSessionStorageItem = (key, defaultValue = null) => {
  const storedValue = sessionStorage.getItem(key);
  return storedValue ? JSON.parse(storedValue) : defaultValue;
};

// Custom Hook to handle map centering
const useMapCenter = (center) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, map.getZoom());
    }
  }, [center, map]);
};

const PullApiTracking = () => {
  const [IMEI, setIMEI] = useState("");
  const location = useLocation();
  const locationPaths = useMemo(() => getSessionStorageItem("Live_Path", []), []);
  const liveLoc = useMemo(() => getSessionStorageItem("Live_Location", {
    latitude: 17.43460111111111,
    longitude: 78.38339555555555,
  }), []);
  
  const [latestLocation, setLatestLocation] = useState([liveLoc.latitude, liveLoc.longitude]);
  const [urlData, setUrlData] = useState(null);
  const formatedPaths = useMemo(() => locationPaths?.map((i) => [i.latitude, i.longitude]), [locationPaths]);
  const [isValidTrackingSession, setIsValidTrackingSession] = useState(false);
  const [center, setCenter] = useState([17.43460111111111, 78.38339555555555]);
  const [startPoint, setStartPoint] = useState(null);
  const [endPoint, setEndPoint] = useState(null);
  const [targetPath, setTargetPath] = useState([]);

  // Map instance ref
  const mapRef = useRef();

  const checkValidTracking = useCallback(async () => {
    try {
      const trackingId = location.pathname.slice(
        location.pathname.lastIndexOf("/pullapitracking") + 17
      );
      const hostUrl = process.env.REACT_APP_API_HOST + "/api/v4/pullapitracking/validate";
      const response = await validateTrackingUrl(trackingId, hostUrl);

      if (response?.data?.status === "SUCCESS") {
        const locationPoints = response.data?.data?.split(",") || [];
        setStartPoint([Number(locationPoints[0].trim()), Number(locationPoints[1].trim())]);
        setEndPoint([Number(locationPoints[2].trim()), Number(locationPoints[3].trim())]);

        if (window.google && window.google.maps) {
          const pathPoints = [[Number(locationPoints[0].trim()), Number(locationPoints[1].trim())]];
          const directionsService = new window.google.maps.DirectionsService();
          const request = {
            origin: { lat: Number(locationPoints[0].trim()), lng: Number(locationPoints[1].trim()) },
            destination: { lat: Number(locationPoints[2].trim()), lng: Number(locationPoints[3].trim()) },
            travelMode: window.google.maps.TravelMode.DRIVING,
          };

          directionsService.route(request, (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK) {
              result.routes[0].legs.forEach(leg => {
                leg.steps.forEach(step => {
                  step.path.forEach(pathPoint => pathPoints.push([pathPoint.lat(), pathPoint.lng()]));
                });
              });
              pathPoints.push([Number(locationPoints[2].trim()), Number(locationPoints[3].trim())]);
              setTargetPath(pathPoints);
            } else {
              console.error(`Error fetching directions: ${status}`);
            }
          });
        } else {
          console.error('Google Maps API is not available.');
        }

        setIMEI(response?.data?.regno);
        setIsValidTrackingSession(true);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error validating tracking session:', error);
      return false;
    }
  }, [location.pathname]);

  const getTrackingHistory = useCallback(async () => {
    try {
      const trackingId = location.pathname.slice(
        location.pathname.lastIndexOf("/pullapitracking") + 17
      );
      const hostUrl = process.env.REACT_APP_API_HOST + "/api/v4/pullapitracking/history";
      const response = await axios.get(`${hostUrl}/${trackingId}`);

      if (response?.data) {
        let previousPoint = response.data[0];
        const historyPoints = response.data.map((i) => {
          const currentPoint = i;
          let distance = 0;
          if (previousPoint.latitude && previousPoint.longitude && currentPoint.latitude && currentPoint.longitude) {
            distance = new LatLonSpherical(
              previousPoint.latitude,
              previousPoint.longitude
            ).distanceTo(new LatLonSpherical(currentPoint.latitude, currentPoint.longitude));
            i.rotation = new LatLonSpherical(
              previousPoint.latitude,
              previousPoint.longitude
            ).initialBearingTo(new LatLonSpherical(currentPoint.latitude, currentPoint.longitude));
            i.distance = distance;
          } else {
            i.distance = 0;
          }
          previousPoint = currentPoint;
          return i;
        }).filter(i => i.distance > 100);
        
        setSessionStorageItem("Live_Path", historyPoints);
        return historyPoints;
      } else {
        setSessionStorageItem("Live_Path", []);
        return [];
      }
    } catch (e) {
      console.error('Error fetching tracking history:', e);
    }
  }, [location.pathname]);

  useEffect(() => {
    const initializeTracking = async () => {
      const isValid = await checkValidTracking();
      if (isValid) {
        await getTrackingHistory();
      }
    };
    initializeTracking();
  }, [checkValidTracking, getTrackingHistory]);

  useEffect(() => {
    let socket = null;
    if (!isValidTrackingSession || !IMEI) return;

    const trackingURL = location.pathname.slice(
      location.pathname.lastIndexOf("/pullapitracking") + 17
    );
    const string = trackingURL.slice(0, 7);

    if (!socket) {
      socket = io(process.env.REACT_APP_SOCKET_SERVER_URL);
    }

    socket.on("connect", () => {
      socket.emit("message", `${string}-${IMEI}`);
    });

    socket.on(`${string}-${IMEI}`, (data) => {
      try {
        const data1 = JSON.parse(data);
        const newLivePath = getSessionStorageItem("Live_Path", []);
        setSessionStorageItem("Live_Path", [...newLivePath, data1]);
        setSessionStorageItem("Last_Location", data1);
        const { latitude, longitude } = data1;
        setCenter([latitude, longitude]);
      } catch (e) {
        console.error(`Error parsing JSON: ${e}`);
      }
    });

    return () => {
      socket.close();
      socket = null;
    };
  }, [isValidTrackingSession, IMEI, location.pathname]);

  const polylineColor = useMemo(() => ({
    color: "rgb(26 131 175 / 80%)",
    fillOpacity: 0.01,
    weight: 7,
  }), []);

  const tripColor = useMemo(() => ({
    color: "blue",
    fillOpacity: 0.01,
    weight: 9,
  }), []);

  useEffect(() => {
    document.title = "SimplyTrack Live Location";
  }, []);

  // Function to capture map and trigger download using leaflet-image
  const handleTakeSnapshot = () => {
    if (mapRef.current) {
      console.log(mapRef.current)
      leafletImage(mapRef.current, function (err, canvas) {
        if (err) {
          console.error("Error taking snapshot:", err);
          return;
        }
        const link = document.createElement("a");
        link.download = "map-snapshot.png";
        link.href = canvas.toDataURL();
        link.click();
      });
    }
  };

  return (
    <>
      {isValidTrackingSession ? (
        <>
          {/* <button onClick={handleTakeSnapshot}>
            Take Snapshot
          </button> */}
          <MapContainer
            center={latestLocation}
            scrollWheelZoom={true}
            zoom={13}
            style={{ height: "100vh", width: "100%" }}
            ref={mapRef} // Assigning ref to map instance
          >
            <useMapCenter center={center} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {formatedPaths && formatedPaths[0] && (
              <Marker
                position={formatedPaths[0]}
                icon={MarkerIcon}
              />
            )}
            {formatedPaths?.length > 0 && (
              <Polyline positions={formatedPaths} pathOptions={polylineColor} />
            )}
            {formatedPaths && formatedPaths[formatedPaths?.length - 1] && (
              <Marker
                position={formatedPaths[formatedPaths?.length - 1]}
                icon={LeafLetMapIconCa}
              />
            )}
            {startPoint && (
              <Marker
                position={startPoint}
                icon={MarkerIcon}
              />
            )}
            {endPoint && (
              <Marker
                position={endPoint}
                icon={StopMarkerIcon}
              />
            )}
            {targetPath && targetPath.length > 0 && (
              <Polyline positions={targetPath} pathOptions={tripColor} />
            )}
          </MapContainer>
        </>
      ) : (
        <h1>Link got Expired</h1>
      )}
    </>
  );
};

export default PullApiTracking;
